/**
 * Shared Storybook utilities — common decorators, application configs, and module metadata presets.
 */
import { applicationConfig, moduleMetadata } from '@storybook/angular';
import { provideHttpClient } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Location } from '@angular/common';
import { LOCALE_ID, PLATFORM_ID } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { UserService } from '../../app/core/services/user.service';
import { CartService } from '../../app/core/services/cart.service';
import { GroupVoucherService } from '../../app/core/services/groupVoucher.service';
import { ProductService } from '../../app/core/services/product.service';
import { ProductPriceService } from '../../app/core/services/product-price.service';
import { StoryblokService } from '../../app/core/services/storyblok.service';
import { StoryblokStylingService } from '../../app/core/services/storyblok-styling.service';
import { TrackEventService } from '../../app/core/services/track-event.service';
import { RoutingService } from '../../app/core/services/routing.service';
import { PageService } from '../../app/core/services/page.service';
import { PrintService } from '../../app/core/services/print.service';
import { StoreConfigService } from '../../app/core/services/store-config.service';
import { GoogleService } from '../../app/core/services/google.service';
import { AlgoliaInsightsService } from '../../app/core/services/algolia-insights.service';
import { CustomerReviewsAndWishlistService } from '../../app/core/services/customer-reviews-and-wishlist.service';
import { CloudflareCacheTagService } from '../../app/core/services/cloudflare-cache-tag.service';
import { StoryblokDynamicInputService } from '../../app/core/services/storyblok-dynamic-input.service';
import { ToastService } from '../../app/core/services/toast.service';
import { GridService } from '../../app/core/services/grid.service';
import { AgeVerificationService } from '../../app/core/services/age-verification.service';
import { HsFormsService } from '../../app/core/services/hs-forms.service';
import { CookiebotLoaderService } from '../../app/core/services/cookiebot-loader.service';
import { CookiebotService } from '../../app/core/services/cookiebot.service';

import {
  MockUserService,
  MockCartService,
  MockGroupVoucherService,
  MockProductService,
  MockProductPriceService,
  MockStoryblokService,
  MockStoryblokStylingService,
  MockTrackEventService,
  MockRoutingService,
  MockPageService,
  MockPrintService,
  MockStoreConfigService,
  MockNgbModal,
  MockGoogleService,
  MockAlgoliaInsightsService,
  MockCustomerReviewsAndWishlistService,
  MockCloudflareCacheTagService,
  MockToastService,
  MockGridService,
  MockNgbActiveModal,
  MockAgeVerificationService,
  MockHsFormsService,
  MockCookiebotLoaderService,
  MockCookiebotService,
  MockStoryblokDynamicInputService,
} from '../_mocks/services.mock';

/**
 * Base application-level providers for Storybook.
 * Use as a decorator: `applicationConfig(baseAppConfig)`
 */
export const baseAppConfig = {
  providers: [
    provideHttpClient(),
    provideRouter([]),
    provideAnimations(),
  ],
};

/**
 * Common service providers used across most component stories.
 * Extend or override individual providers in specific stories.
 */
export const commonProviders = [
  { provide: UserService, useClass: MockUserService },
  { provide: CartService, useClass: MockCartService },
  { provide: GroupVoucherService, useClass: MockGroupVoucherService },
  { provide: ProductService, useClass: MockProductService },
  { provide: ProductPriceService, useClass: MockProductPriceService },
  { provide: StoryblokService, useClass: MockStoryblokService },
  { provide: StoryblokStylingService, useClass: MockStoryblokStylingService },
  { provide: TrackEventService, useClass: MockTrackEventService },
  { provide: RoutingService, useClass: MockRoutingService },
  { provide: PageService, useClass: MockPageService },
  { provide: PrintService, useClass: MockPrintService },
  { provide: StoreConfigService, useClass: MockStoreConfigService },
  { provide: GoogleService, useClass: MockGoogleService },
  { provide: AlgoliaInsightsService, useClass: MockAlgoliaInsightsService },
  { provide: CustomerReviewsAndWishlistService, useClass: MockCustomerReviewsAndWishlistService },
  { provide: CloudflareCacheTagService, useClass: MockCloudflareCacheTagService },
  { provide: ToastService, useClass: MockToastService },
  { provide: NgbModal, useClass: MockNgbModal },
  { provide: NgbActiveModal, useClass: MockNgbActiveModal },
  { provide: GridService, useClass: MockGridService },
  { provide: AgeVerificationService, useClass: MockAgeVerificationService },
  { provide: HsFormsService, useClass: MockHsFormsService },
  { provide: CookiebotLoaderService, useClass: MockCookiebotLoaderService },
  { provide: CookiebotService, useClass: MockCookiebotService },
  { provide: StoryblokDynamicInputService, useClass: MockStoryblokDynamicInputService },
  { provide: Location, useValue: { back() {}, forward() {} } },
  { provide: PLATFORM_ID, useValue: 'browser' },
  { provide: LOCALE_ID, useValue: 'da-DK' },
];

/**
 * Pre-built decorator combining applicationConfig + common providers.
 * Usage in a story meta:
 *
 * ```ts
 * decorators: [
 *   ...withCommonDecorators(),
 *   // add more decorators if needed
 * ]
 * ```
 */
export function withCommonDecorators(extraProviders: any[] = []) {
  return [
    applicationConfig(baseAppConfig),
    moduleMetadata({
      providers: [...commonProviders, ...extraProviders],
    }),
  ];
}

/**
 * Pre-built decorator for Storyblok components.
 * Includes common Storyblok-specific service mocks.
 */
export function withStoryblokDecorators(extraProviders: any[] = []) {
  return [
    applicationConfig(baseAppConfig),
    moduleMetadata({
      providers: [
        ...commonProviders,
        ...extraProviders,
      ],
    }),
  ];
}
